import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import CumeoMatch from "../CumeoMatch";
import LoadMoreButton from "../LoadMoreButton";
import { BANG_KEO_PAGE_LIMIT } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvLiveScoreV14 = ({ type = "all" }) => {
  const {
    colorPrimary,
    colorSecondary,
    colorBg,
    colorText,
    colorForeground,
    colorBorder,
    colorAccent,
    colorTextMuted,
    colorTextSecondary,
    colorOnPrimary,
    colorBgHover,
    colorHalfScore,
    colorGradientStart,
    colorGradientEnd,
    colorLiveAlt,
    hasDataConfig,
  } = useCompConfig();

  const themeStyle = hasDataConfig
    ? {
        "--theme-primary": colorPrimary,
        "--theme-highlight": colorSecondary,
        "--theme-bg": colorBg,
        "--theme-text": colorText,
        "--theme-foreground": colorForeground,
        ...(colorBorder != null && { "--theme-border": colorBorder }),
        ...(colorAccent != null && { "--theme-accent": colorAccent }),
        ...(colorTextMuted != null && { "--theme-text-muted": colorTextMuted }),
        ...(colorTextSecondary != null && {
          "--theme-text-secondary": colorTextSecondary,
        }),
        ...(colorOnPrimary != null && { "--theme-on-primary": colorOnPrimary }),
        ...(colorBgHover != null && { "--theme-bg-hover": colorBgHover }),
        ...(colorHalfScore != null && { "--text-half-score": colorHalfScore }),
        ...(colorGradientStart != null && {
          "--theme-gradient-start": colorGradientStart,
        }),
        ...(colorGradientEnd != null && {
          "--theme-gradient-end": colorGradientEnd,
        }),
        ...(colorLiveAlt != null && { "--theme-live-alt": colorLiveAlt }),
      }
    : {};

  const [displayLimit, setDisplayLimit] = useState(20);

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const baseQuery = {
    include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
    filterOdds: -1,
    cache: false,
    page: 1,
  };

  const queryLive = useMemo(
    () => ({
      ...baseQuery,
      filterInclude: `live`,
      status: "live",
      limit: 100,
    }),
    [],
  );

  const queryObject = useMemo(
    () => ({
      ...baseQuery,
      filterInclude: `date:${todayString}`,
      status: type === "ketqua" ? "end" : "",
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [todayString, type],
  );

  const {
    leaguesWithMatches, // Represents today's matches
    loading: todayLoading,
    error: todayError,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const { leaguesWithMatches: liveLeagues, loading: liveLoading } =
    useBangKeoMatches(queryLive, false);

  const loading = todayLoading || liveLoading;
  const error = todayError;

  const baseLeaguesWithMatches = useMemo(() => {
    let allData = [];

    // 1. Add live data (Always First)
    (liveLeagues || []).forEach((item) => {
      allData.push({ ...item, matches: [...(item.matches || [])] });
    });

    // 2. Add today data, merging matches if league exists
    (leaguesWithMatches || []).forEach((item) => {
      const exist = allData.find(
        (d) => String(d.league?.id) === String(item.league?.id),
      );
      if (exist) {
        const newMatches = item.matches || [];
        const existingMatches = exist.matches || [];
        const diff = newMatches.filter(
          (m) => !existingMatches.find((em) => em.id === m.id),
        );
        exist.matches = [...existingMatches, ...diff];
      } else {
        allData.push({ ...item, matches: [...(item.matches || [])] });
      }
    });

    return allData.map((item) => {
      const normalizedMatches = Array.isArray(item?.matches)
        ? item.matches
        : Object.values(item?.matches || {});
      return {
        ...item,
        matches: normalizedMatches.filter(Boolean),
      };
    });
  }, [liveLeagues, leaguesWithMatches]);

  const allFilteredMatches = useMemo(() => {
    return baseLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [baseLeaguesWithMatches]);

  useEffect(() => {
    // Auto-fetch if filtered results are below display limit
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      baseLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [baseLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  return (
    <div
      data-theme="orange-dark-slate"
      className="max-w-[1280px] mx-auto flex min-h-screen flex-col gap-3 rounded-xl px-2 md:px-2 py-3 bg-[var(--theme-bg)] box-border"
      style={{
        ...themeStyle,
      }}
    >
      <div className="flex flex-col gap-3 m-0 p-0 box-border">
        {loading && <Loading />}
        {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
        {!loading && (
          <>
            <div className="w-full bg-[var(--theme-foreground)] overflow-hidden rounded-md border border-solid border-[var(--theme-border)] m-0 p-0 box-border border-0">
              <div className="mb-hidden md:grid grid-cols-[70px_2.5fr_5fr_6fr] gap-4 px-2 py-3 text-xs font-semibold text-[var(--theme-text)] uppercase border-b  border-r border-l border-t border-solid border-[var(--theme-border)] items-center bg-[var(--theme-accent)] m-0 box-border border-0">
                <div className="m-0 p-0 text-start">Thời Gian</div>
                <div className="m-0 p-0">Giải Đấu</div>
                <div className="m-0 p-0">Trận Đấu</div>
                <div className="grid grid-cols-3 gap-0.5 m-0 p-0 box-border">
                  <div className="text-center m-0 p-0">Cược Chấp</div>
                  <div className="text-center m-0 p-0">Tài/Xỉu</div>
                  <div className="text-center m-0 p-0">1X2</div>
                </div>
              </div>

              <div className="flex flex-col m-0 p-0">
                {allFilteredMatches.length === 0 ? (
                  <div className="p-10">
                    <NoData
                      message={
                        isLoadingMore
                          ? "Đang tìm thêm trận đấu phù hợp..."
                          : "Không tìm thấy trận đấu theo tiêu chí lọc"
                      }
                    />
                  </div>
                ) : (
                  allFilteredMatches.map((match, mIdx) => (
                    <CumeoMatch
                      key={match?.id || `match-${mIdx}`}
                      match={match}
                    />
                  ))
                )}
              </div>
            </div>

            <LoadMoreButton
              isLoading={isLoadingMore}
              onClick={() => setDisplayLimit((prev) => prev + 20)}
              loadingComponent={<Loading />}
              hasMore={allFilteredMatches.length > displayLimit || hasMore}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default KbvLiveScoreV14;
